import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import {
  PaymentType,
  ReconciliationSessionStatus,
  ReconciliationSessionSummaryFragment,
  useCommitReconciliationSessionMutation,
  useClearReconciliationSessionItemsMutation,
  useDiscardReconciliationSessionMutation,
  useFetchMyReconciliationSessionsLazyQuery,
  useFetchReconciliationSessionLazyQuery,
  useStartOrResumeReconciliationSessionMutation,
  useUpdateReconciliationSessionMutation,
  useUpdateReconciliationSessionViewPreferencesMutation,
  ReconciliationSessionViewPrefsFragment,
  ReconciliationSessionViewPreferencesInput
} from '../../../../generated'
import { Alert } from '../../common/Alert'

export type SessionItem = { paymentId: string; amount: number; paymentType: string }
/** List view/sort settings remembered on the session (see the entity's ReconciliationSessionViewPreferences). */
export type SessionViewPreferences = ReconciliationSessionViewPrefsFragment
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'unsaved' | 'error'

const AUTOSAVE_DEBOUNCE_MS = 2000

const SESSION_GONE_MESSAGE =
  'That reconciliation session no longer exists. Your selection on screen is unchanged — start a new session to save it.'

/** Normalize any date in a month to that month's 1st as `YYYY-MM-DD`, matching how the session stores it. */
const normalizeMonth = (month: string): string => dayjs(month).startOf('month').format('YYYY-MM-DD')

/**
 * True when the API says the session is gone (404) or isn't ours (403) — i.e. the id the screen is holding is
 * dead. Distinguished from transient failures (network, 500) because those must NOT tear down local state:
 * here the only recovery is to stop pointing at it, whereas a blip should just be retried.
 */
const isSessionGone = (err: any): boolean => {
  const extensions = err?.graphQLErrors?.[0]?.extensions
  const code = Number(extensions?.['code'])
  const statusCode = Number((extensions?.['response'] as { statusCode?: number })?.statusCode)
  return code === 404 || code === 403 || statusCode === 404 || statusCode === 403
}

interface Params {
  accountId: string | null
  statementMonth: string
  /** Live selected items (id -> item) so the debounced save reads the latest values, not a stale closure. */
  currentItemsRef: MutableRefObject<Map<string, SessionItem>>
  /** Live statement balance (or null if not entered). */
  currentBalanceRef: MutableRefObject<number | null>
  /** Called when a session loads so the parent can hydrate its local selection + balance + statement month
   * + the list view/sort settings the session was last worked on with. */
  onHydrate: (
    items: SessionItem[],
    statementBalance: number | null,
    statementMonth: string,
    viewPreferences: SessionViewPreferences | null
  ) => void
  /** Called after a successful commit/discard/clear (or switching to an account with no session). */
  onResetLocal: () => void
}

/**
 * Server-backed reconciliation session: start/resume for (account, month), persist the selection via
 * delta MERGE (never blind replace), auto-save (debounced) + explicit save, and commit/discard/clear.
 */
export const useReconciliationSession = ({
  accountId,
  statementMonth,
  currentItemsRef,
  currentBalanceRef,
  onHydrate,
  onResetLocal
}: Params) => {
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [hydrating, setHydrating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [sessions, setSessions] = useState<ReconciliationSessionSummaryFragment[]>([])
  // Statement month the active session is persisted against (normalized `YYYY-MM-DD`), or null when no session.
  // The screen compares this with the viewed month to prompt the user before changing it — never auto-changed.
  const [sessionMonth, setSessionMonth] = useState<string | null>(null)
  // Bank account the active session belongs to (for the month-change prompt + the status bar), null when none.
  const [sessionAccountName, setSessionAccountName] = useState<string | null>(null)
  const [sessionAccountSortCode, setSessionAccountSortCode] = useState<string | null>(null)
  // Server-computed credit/debit totals of the WHOLE selection (all types, independent of the on-screen Type
  // filter and of which rows are loaded). The screen falls back to these whenever it cannot total the
  // selection per payment itself — chiefly the day-grouped view, whose rows only carry a day's NET.
  const [sessionCreditTotal, setSessionCreditTotal] = useState(0)
  const [sessionDebitTotal, setSessionDebitTotal] = useState(0)
  // The session's stored list view/sort settings, as last read from (or written to) the server. Doubles as the
  // baseline the screen compares against, so re-applying them on hydrate doesn't bounce straight back as a save.
  const [sessionPreferences, setSessionPreferences] = useState<SessionViewPreferences | null>(null)

  const versionRef = useRef(0)
  const lastSavedItemsRef = useRef<Map<string, SessionItem>>(new Map())
  const lastSavedBalanceRef = useRef<number | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savingRef = useRef(false)
  const sessionIdRef = useRef<number | null>(null)
  sessionIdRef.current = sessionId
  // Latest saveNow, so the account-switch cleanup can flush the outgoing session.
  const saveNowRef = useRef<(() => Promise<boolean>) | null>(null)
  // Latest statement month, read by the explicit startSession (which uses the currently-viewed month).
  const monthRef = useRef(statementMonth)
  monthRef.current = statementMonth

  const [startOrResume] = useStartOrResumeReconciliationSessionMutation()
  const [updateSession] = useUpdateReconciliationSessionMutation()
  const [updatePreferences] = useUpdateReconciliationSessionViewPreferencesMutation()
  const [clearSession] = useClearReconciliationSessionItemsMutation()
  const [commitSession] = useCommitReconciliationSessionMutation()
  const [discardSession] = useDiscardReconciliationSessionMutation()
  const [fetchSessions] = useFetchMyReconciliationSessionsLazyQuery({ fetchPolicy: 'network-only' })
  const [fetchFullSession] = useFetchReconciliationSessionLazyQuery({ fetchPolicy: 'network-only' })

  const refreshSessions = useCallback(() => {
    // Swallow failures: this only keeps the Sessions list fresh and is fired from teardown paths (leave,
    // detach-on-404) where an unhandled rejection would surface as a spurious error on an otherwise fine flow.
    fetchSessions()
      .then(res => setSessions(res.data?.fetchMyReconciliationSessions ?? []))
      .catch(() => undefined)
  }, [fetchSessions])

  const hydrateFrom = useCallback(
    (s: {
      id: number
      version: number
      statementBalance?: number | null
      statementMonth: string
      items: SessionItem[]
      creditTotal?: number
      debitTotal?: number
      removedItemCount?: number | null
      viewPreferences?: SessionViewPreferences | null
      account?: { name?: string | null; sortCode?: string | null } | null
    }) => {
      setSessionId(s.id)
      versionRef.current = s.version
      const items = (s.items || []).map(it => ({
        paymentId: it.paymentId,
        amount: Number(it.amount),
        paymentType: String(it.paymentType)
      }))
      lastSavedItemsRef.current = new Map(items.map(it => [it.paymentId, it]))
      lastSavedBalanceRef.current = s.statementBalance ?? null
      setSessionMonth(normalizeMonth(s.statementMonth))
      setSessionAccountName(s.account?.name ?? null)
      setSessionAccountSortCode(s.account?.sortCode ?? null)
      setSessionCreditTotal(s.creditTotal ?? 0)
      setSessionDebitTotal(s.debitTotal ?? 0)
      // Set BEFORE onHydrate: the screen writes these settings into the URL, then compares the URL against
      // this baseline to decide whether to persist a change. Stale baseline = a pointless save on every load.
      setSessionPreferences(s.viewPreferences ?? null)
      onHydrate(items, s.statementBalance ?? null, s.statementMonth, s.viewPreferences ?? null)
      setSaveStatus('saved')
      // The server drops selections that stopped being eligible (reconciled elsewhere, marked reconciled in AL,
      // returned, deleted). They're gone from the listing too, so say why the selection shrank.
      if (s.removedItemCount) {
        console.log(
          `Removed ${s.removedItemCount} transaction(s) from this session because they are no longer available to reconcile.`
        )
      }
    },
    [onHydrate]
  )

  /** Tear down every trace of the active session locally (no server call, no on-screen selection change). */
  const clearLocalSession = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setSessionId(null)
    sessionIdRef.current = null
    versionRef.current = 0
    lastSavedItemsRef.current = new Map()
    lastSavedBalanceRef.current = null
    setSessionMonth(null)
    setSessionAccountName(null)
    setSessionAccountSortCode(null)
    setSessionCreditTotal(0)
    setSessionDebitTotal(0)
    setSessionPreferences(null)
    setSaveStatus('idle')
  }, [])

  /**
   * Fetch a session and hydrate the screen from it.
   * 'not-open' — committed or discarded elsewhere; 'gone' — the row no longer exists (or isn't ours);
   * 'error' — transient, leave local state alone. Only the caller knows how to word it for the user.
   */
  const loadSession = useCallback(
    async (sessionId: number): Promise<'ok' | 'not-open' | 'gone' | 'error'> => {
      try {
        const full = await fetchFullSession({ variables: { sessionId } })
        const s = full.data?.fetchReconciliationSession
        if (!s) return 'error'
        if (s.status !== ReconciliationSessionStatus.Open) return 'not-open'
        hydrateFrom(s)
        return 'ok'
      } catch (err: any) {
        if (isSessionGone(err)) return 'gone'
        Alert.error(err?.message || 'Could not load the session')
        return 'error'
      }
    },
    [fetchFullSession, hydrateFrom]
  )

  // Resolve the EXISTING open session for this account (read-only — never auto-creates). Creation is explicit
  // via startSession(), so merely browsing the screen does not spawn sessions. If an open session exists, load
  // it (which also switches the view to its statement month); otherwise clear local state and wait for the user.
  useEffect(() => {
    if (!accountId) {
      setSessionId(null)
      return
    }
    setHydrating(true)
    fetchSessions()
      .then(res => {
        const list = res.data?.fetchMyReconciliationSessions ?? []
        setSessions(list)
        const match = list.find(s => s.account?.id === accountId)
        if (match) {
          // Through loadSession so a session that vanished or closed between listing it and loading it is
          // handled like everywhere else, instead of surfacing a raw "session not found".
          return loadSession(match.id).then(result => {
            if (result === 'ok') return
            clearLocalSession()
            onResetLocal()
            if (result === 'gone' || result === 'not-open') refreshSessions()
          })
        }
        // No open session for this account — reset local so the previous account's browsing state doesn't linger.
        clearLocalSession()
        onResetLocal()
      })
      .catch(err => Alert.error(err?.message || 'Could not load reconciliation sessions'))
      .finally(() => setHydrating(false))
    // Flush the OUTGOING session before switching account, so a pending edit isn't lost. At cleanup time the
    // refs still hold the previous session's values (save old→old); the sessionId guard in saveNow prevents
    // clobbering the newly-hydrated baseline.
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveNowRef.current?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId])

  const buildDelta = () => {
    const current = currentItemsRef.current
    const lastSaved = lastSavedItemsRef.current
    const add: SessionItem[] = []
    current.forEach((item, id) => {
      if (!lastSaved.has(id)) add.push(item)
    })
    const remove: string[] = []
    lastSaved.forEach((_item, id) => {
      if (!current.has(id)) remove.push(id)
    })
    const balance = currentBalanceRef.current
    const balanceChanged = balance !== lastSavedBalanceRef.current
    const sent = new Map(current)
    return { add, remove, balance, balanceChanged, sent, hasChanges: add.length > 0 || remove.length > 0 || balanceChanged }
  }

  const saveNow = useCallback(async (): Promise<boolean> => {
    const id = sessionIdRef.current
    if (!id || savingRef.current) return false
    const delta = buildDelta()
    if (!delta.hasChanges) {
      setSaveStatus('saved')
      return true
    }
    savingRef.current = true
    setSaveStatus('saving')
    try {
      const res = await updateSession({
        variables: {
          input: {
            sessionId: id,
            add: delta.add,
            remove: delta.remove,
            ...(delta.balanceChanged ? { statementBalance: delta.balance } : {}),
            version: versionRef.current
          } as any
        }
      })
      const s = res.data?.updateReconciliationSession
      // Only advance the baseline if we're still on the same session — otherwise an account/month switch may
      // have hydrated a new session whose baseline this stale save must not clobber.
      if (s && sessionIdRef.current === id) {
        versionRef.current = s.version
        lastSavedItemsRef.current = delta.sent
        if (delta.balanceChanged) lastSavedBalanceRef.current = delta.balance
        setSessionCreditTotal(s.creditTotal ?? 0)
        setSessionDebitTotal(s.debitTotal ?? 0)
        setSaveStatus('saved')
      }
      return true
    } catch (err: any) {
      if (isSessionGone(err)) {
        // The session no longer exists (or isn't ours). Every retry would fail the same way, so detach instead
        // of leaving the screen attached to a dead id and erroring on a loop. The on-screen selection is kept
        // — the user can start a fresh session and keep it.
        clearLocalSession()
        refreshSessions()
        Alert.error(SESSION_GONE_MESSAGE)
        return false
      }
      setSaveStatus('error')
      Alert.error(err?.message || 'Could not save the session')
      return false
    } finally {
      savingRef.current = false
      // Something changed while we were saving → schedule another pass. Skipped once the session is detached,
      // otherwise the status would flip back to "Unsaved changes" with nothing to save it to.
      if (sessionIdRef.current && buildDelta().hasChanges) scheduleSave()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSession, clearLocalSession, refreshSessions])
  saveNowRef.current = saveNow

  const scheduleSave = useCallback(() => {
    setSaveStatus('unsaved')
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveNow()
    }, AUTOSAVE_DEBOUNCE_MS)
  }, [saveNow])

  /** Call whenever the local selection or balance changes to schedule a debounced save. */
  const markDirty = useCallback(() => {
    if (!sessionIdRef.current) return
    const { hasChanges } = buildDelta()
    if (hasChanges) scheduleSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSave])

  /** Force an immediate flush (explicit Save button, before commit, on unload). */
  const flush = useCallback(async () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    return saveNow()
  }, [saveNow])

  /** Re-fetch the active session and hydrate from it, dropping any pending local delta. Use after something
   * outside the screen changed it server-side (e.g. marking payments reconciled in AL, or a rejected commit
   * that pruned stale items) — the screen has to adopt the server's item set AND version, or its next save
   * would conflict and the totals would keep showing transactions that are no longer there. */
  const reload = useCallback(async (): Promise<boolean> => {
    const id = sessionIdRef.current
    if (!id) return false
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    const result = await loadSession(id)
    if (result === 'not-open' || result === 'gone') {
      // Finalized/discarded elsewhere, or the row is gone entirely — staying pointed at it would leave the
      // screen attached to a session that rejects every save. Drop back to "no session" instead.
      clearLocalSession()
      refreshSessions()
      if (result === 'gone') Alert.error(SESSION_GONE_MESSAGE)
    }
    return result === 'ok'
  }, [loadSession, clearLocalSession, refreshSessions])

  /** Resume an open session explicitly. The auto-resolve effect only fires when the ACCOUNT changes, so
   * re-entering a session for the bank already on screen (after leaving it) has to be driven by hand. */
  const enterSession = useCallback(
    async (sessionId: number): Promise<boolean> => {
      setBusy(true)
      try {
        const result = await loadSession(sessionId)
        if (result === 'not-open' || result === 'gone') {
          // The list this was picked from is stale — refresh it so the dead entry stops being offered.
          Alert.warning(result === 'gone' ? SESSION_GONE_MESSAGE : 'That session is no longer open.')
          refreshSessions()
        }
        return result === 'ok'
      } finally {
        setBusy(false)
      }
    },
    [loadSession, refreshSessions]
  )

  /** Step out of the active session WITHOUT discarding it: flush pending edits, then detach locally and clear
   * the on-screen ticks. The draft stays open on the server and can be resumed from the Sessions menu.
   * The screen has a single selection, so while a session owns it, one-off actions that act on "everything
   * selected" (marking payments reconciled in AL) would sweep up the entire draft — leaving is the way out. */
  const leaveSession = useCallback(async (): Promise<boolean> => {
    if (!sessionIdRef.current) return false
    setBusy(true)
    try {
      await flush()
      // clearLocalSession nulls sessionIdRef synchronously, so the selection reset below can't schedule a
      // stray autosave against the session we just stepped out of.
      clearLocalSession()
      onResetLocal()
      refreshSessions()
      return true
    } finally {
      setBusy(false)
    }
  }, [flush, clearLocalSession, onResetLocal, refreshSessions])

  const commit = useCallback(async (): Promise<boolean> => {
    const id = sessionIdRef.current
    if (!id) return false
    setBusy(true)
    try {
      await flush()
      await commitSession({ variables: { sessionId: id } })
      // The session is now COMMITTED (no longer OPEN). Tear the local session state down BEFORE clearing the
      // on-screen selection: onResetLocal empties the selection, which schedules a debounced autosave (a
      // `remove` delta). If sessionId still pointed at the committed session that save would hit the server and
      // fail repeatedly with "This reconciliation session is no longer open". Nulling sessionIdRef synchronously
      // makes markDirty/saveNow bail; clearing the baseline + timer guarantees no stray delta fires.
      clearLocalSession()
      onResetLocal()
      refreshSessions()
      Alert.success('Reconciliation committed successfully')
      return true
    } catch (err: any) {
      const exception = err?.graphQLErrors?.[0]?.extensions?.['exception'] as
        | { response?: { ineligiblePaymentIds?: string[] } }
        | undefined
      const ineligible = exception?.response?.ineligiblePaymentIds ?? []
      Alert.error(
        ineligible.length
          ? `${ineligible.length} selected payment(s) are no longer eligible. Reload the list and try again.`
          : err?.message || 'Could not commit the reconciliation'
      )
      // The rejected commit may have pruned stale items server-side (and bumped the version), so pull the
      // session back down — otherwise the screen keeps totals for transactions that are no longer in it.
      await reload()
      return false
    } finally {
      setBusy(false)
    }
  }, [flush, commitSession, onResetLocal, refreshSessions, reload, clearLocalSession])

  const discard = useCallback(async (): Promise<boolean> => {
    const id = sessionIdRef.current
    if (!id) return false
    setBusy(true)
    try {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      await discardSession({ variables: { sessionId: id } })
      onResetLocal()
      setSessionId(null)
      setSessionMonth(null)
      setSessionAccountName(null)
      setSessionAccountSortCode(null)
      setSessionCreditTotal(0)
      setSessionDebitTotal(0)
      setSessionPreferences(null)
      refreshSessions()
      return true
    } catch (err: any) {
      // Already gone server-side — there's nothing left to discard, so finish the teardown locally rather
      // than leaving the screen attached to a session that can't be acted on.
      if (isSessionGone(err)) {
        clearLocalSession()
        onResetLocal()
        refreshSessions()
        return true
      }
      Alert.error(err?.message || 'Could not discard the session')
      return false
    } finally {
      setBusy(false)
    }
  }, [discardSession, onResetLocal, refreshSessions, clearLocalSession])

  /** Start Over: empty the current session's items + balance but keep it open. */
  const clear = useCallback(async (): Promise<boolean> => {
    const id = sessionIdRef.current
    if (!id) return false
    setBusy(true)
    try {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      const res = await clearSession({ variables: { input: { sessionId: id, version: versionRef.current } } })
      const s = res.data?.clearReconciliationSessionItems
      if (s) {
        versionRef.current = s.version
        lastSavedItemsRef.current = new Map()
        lastSavedBalanceRef.current = null
        setSessionCreditTotal(0)
        setSessionDebitTotal(0)
      }
      onResetLocal()
      setSaveStatus('saved')
      return true
    } catch (err: any) {
      Alert.error(err?.message || 'Could not clear the session')
      return false
    } finally {
      setBusy(false)
    }
  }, [clearSession, onResetLocal])

  /** Persist a new statement month onto the OPEN session. Explicit user action (confirmed) — the month is never
   * changed automatically. Flushes any pending item/balance edits first so their version isn't invalidated by
   * the month bump. */
  const changeMonth = useCallback(
    async (month: string): Promise<boolean> => {
      const id = sessionIdRef.current
      if (!id) return false
      const normalized = normalizeMonth(month)
      setBusy(true)
      try {
        await flush()
        await updateSession({
          variables: { input: { sessionId: id, statementMonth: normalized, version: versionRef.current } as any }
        })
        // The server drops any selected transactions dated after the new month end, so re-hydrate the whole
        // session — this trims the local selection + checkboxes and refreshes the month/totals to match.
        const full = await fetchFullSession({ variables: { sessionId: id } })
        const s = full.data?.fetchReconciliationSession
        if (s) hydrateFrom(s)
        return true
      } catch (err: any) {
        Alert.error(err?.message || 'Could not change the session month')
        return false
      } finally {
        setBusy(false)
      }
    },
    [flush, updateSession, fetchFullSession, hydrateFrom]
  )

  /** Explicitly create (or resume) the session for the current account+month, persisting the current local
   * selection as the initial delta. Sessions are ONLY created here — never automatically on screen load. */
  const startSession = useCallback(async (): Promise<boolean> => {
    if (!accountId) return false
    setBusy(true)
    try {
      const res = await startOrResume({ variables: { input: { accountId, statementMonth: monthRef.current } } })
      const s = res.data?.startOrResumeReconciliationSession
      if (!s) return false
      setSessionId(s.id)
      sessionIdRef.current = s.id // set synchronously so the flush below targets the new session
      versionRef.current = s.version
      lastSavedItemsRef.current = new Map(
        (s.items || []).map(it => [
          it.paymentId,
          { paymentId: it.paymentId, amount: Number(it.amount), paymentType: String(it.paymentType) }
        ])
      )
      lastSavedBalanceRef.current = s.statementBalance ?? null
      setSessionMonth(normalizeMonth(s.statementMonth))
      setSessionAccountName(s.account?.name ?? null)
      setSessionAccountSortCode(s.account?.sortCode ?? null)
      setSessionCreditTotal(s.creditTotal ?? 0)
      setSessionDebitTotal(s.debitTotal ?? 0)
      setSessionPreferences(s.viewPreferences ?? null)
      // Keep the current local selection and flush it as the initial adds.
      await flush()
      refreshSessions()
      setSaveStatus('saved')
      return true
    } catch (err: any) {
      Alert.error(err?.message || 'Could not start the session')
      return false
    } finally {
      setBusy(false)
    }
  }, [accountId, startOrResume, flush, refreshSessions])

  /**
   * Remember the current list view/sort settings on the session. Fire-and-forget: this is cosmetic, so a
   * failure is swallowed rather than surfaced — it must never interrupt a reconciliation. It also carries no
   * `version` and touches only its own column server-side, so it can't collide with the selection autosave.
   */
  const savePreferences = useCallback(
    async (preferences: ReconciliationSessionViewPreferencesInput): Promise<boolean> => {
      const id = sessionIdRef.current
      if (!id) return false
      try {
        const res = await updatePreferences({ variables: { input: { sessionId: id, preferences } } })
        const saved = res.data?.updateReconciliationSessionViewPreferences
        // Guard against a switch mid-flight: the answer belongs to the session we asked about, not the new one.
        if (saved && sessionIdRef.current === id) setSessionPreferences(saved)
        return true
      } catch {
        return false
      }
    },
    [updatePreferences]
  )

  // Best-effort flush when the tab is hidden/closed.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') saveNow()
    }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [saveNow])

  return {
    sessionId,
    hasSession: sessionId !== null,
    sessionMonth,
    sessionAccountName,
    sessionAccountSortCode,
    sessionCreditTotal,
    sessionDebitTotal,
    sessionPreferences,
    savePreferences,
    saveStatus,
    hydrating,
    busy,
    sessions,
    refreshSessions,
    markDirty,
    flush,
    reload,
    enterSession,
    leaveSession,
    startSession,
    changeMonth,
    commit,
    discard,
    clear
  }
}

// Re-export so the parent can label the derived PaymentType values without importing the enum separately.
export const CREDIT = PaymentType.Credit
export const DEBIT = PaymentType.Debit
